import groundcover from './groundcover-browser';

/**
 * The SDK flattens nested attributes before handing the event to beforeSend, so a stack
 * frame arrives as `{ 'error_stacktrace[0].filename': '...', ... }` rather than the
 * `{ filename, ... }` shape its own types advertise. Read frames through
 * `frameFilename` instead of touching keys directly.
 */
type RawStackFrame = Record<string, unknown>;

export type ExceptionAttributes = {
	error_type?: string;
	error_message?: string;
	error_fingerprint?: string;
	error_handled?: boolean;
	error_stacktrace?: Array<RawStackFrame>;
	error_duplicates_suppressed?: number;
};

const frameFilename = (frame: RawStackFrame): string => {
	const key = Object.keys(frame).find((attribute) => attribute === 'filename' || attribute.endsWith('.filename'));
	const filename = key ? frame[key] : undefined;

	return typeof filename === 'string' && filename !== 'unknown' ? filename : '';
};

// Groundcover listens on window.onerror/unhandledrejection, so anything a browser
// extension injects into the page reports as if it were ours. Extension frames are
// identifiable by their script URL scheme.
const NON_APP_SCRIPT_SCHEMES = [
	'chrome-extension://',
	'moz-extension://',
	'safari-extension://',
	'safari-web-extension://',
	'ms-browser-extension://',
	'extensions::',
	'chrome://',
	'resource://',
	'webkit-masked-url://',
	'file://',
];

// Our bundle is served from VITE_PUBLIC_URL (a static.* host) in deployed
// environments and from the page origin when running locally.
const APP_SCRIPT_BASE = (import.meta.env.VITE_PUBLIC_URL as string | undefined)?.replace(/\/+$/, '')
	|| (typeof window === 'undefined' ? '' : window.location.origin);

const isExtensionFrame = (filename: string) => NON_APP_SCRIPT_SCHEMES.some((scheme) => filename.startsWith(scheme));

const isAppFrame = (filename: string) => filename.startsWith('/')
	|| (!!APP_SCRIPT_BASE && filename.startsWith(APP_SCRIPT_BASE));

/**
 * Keep an exception only when it can be traced back to our own code: the stack has to
 * run through our bundle, and the throwing frame must not be extension code. Errors
 * with no attributable stack (cross-origin scripts, inline snippets injected into the
 * document, extension code that strips its own frames) are treated as global-scope
 * noise and dropped.
 */
const isAppException = (attributes: ExceptionAttributes) => {
	// Anything we reported ourselves via captureException is ours by definition.
	if (attributes.error_handled) return true;

	const filenames = (attributes.error_stacktrace ?? []).map(frameFilename).filter(Boolean);
	if (!filenames.length) return false;

	// filenames[0] is where the error was thrown (error-stack-parser keeps innermost first).
	if (isExtensionFrame(filenames[0])) return false;

	return filenames.some(isAppFrame);
};

// A single runaway error — a ResizeObserver notification loop, a render that throws on
// every frame — can fire hundreds of times a second. Report the first few of a burst so
// the problem is still visible, then hold off until the window rolls over.
const ERROR_BURST_LIMIT = 3;
const ERROR_BURST_WINDOW_MS = 10_000;
const ERROR_KEYS_TRACKED_MAX = 100;

type ErrorBurst = { windowStart: number; sent: number; suppressed: number };

const errorBursts = new Map<string, ErrorBurst>();

// error_fingerprint is `${error name}:${truncated message}`, which is exactly the
// granularity we want to collapse on.
const errorKey = (attributes: ExceptionAttributes) => attributes.error_fingerprint
	|| `${attributes.error_type ?? 'Error'}:${attributes.error_message ?? ''}`;

// Distinct messages (anything interpolating an id or a URL) would otherwise accumulate
// keys for the life of the page.
const pruneErrorBursts = (now: number) => {
	if (errorBursts.size <= ERROR_KEYS_TRACKED_MAX) return;

	errorBursts.forEach((burst, key) => {
		if (now - burst.windowStart >= ERROR_BURST_WINDOW_MS) errorBursts.delete(key);
	});

	if (errorBursts.size > ERROR_KEYS_TRACKED_MAX) errorBursts.clear();
};

export const shouldSendException = (attributes: ExceptionAttributes, now = Date.now()) => {
	if (!isAppException(attributes)) return false;

	const key = errorKey(attributes);
	const burst = errorBursts.get(key);

	if (!burst || now - burst.windowStart >= ERROR_BURST_WINDOW_MS) {
		// Carry the tally forward so a suppressed burst is still countable in RUM.
		if (burst?.suppressed) attributes.error_duplicates_suppressed = burst.suppressed;
		errorBursts.set(key, { windowStart: now, sent: 1, suppressed: 0 });
		pruneErrorBursts(now);

		return true;
	}

	if (burst.sent < ERROR_BURST_LIMIT) {
		burst.sent += 1;

		return true;
	}

	burst.suppressed += 1;

	return false;
};

// Streaming playback re-requests the manifest every few seconds and pulls a segment
// per few seconds of video, which buries everything else in RUM: manifests (.m3u8),
// segments (.ts, .m4s) and caption tracks (.vtt). Matched against the raw request URL,
// so allow for a query string or fragment after the extension.
const MEDIA_SEGMENT_URL = /\.(m3u8|ts|m4s|vtt)(\?|#|$)/i;

const GC_ENABLED = import.meta.env.VITE_STAGE && import.meta.env.VITE_GROUNDCOVER_DSN && import.meta.env.VITE_GROUNDCOVER_API_KEY;
if (GC_ENABLED) {
	groundcover.init({
		apiKey: import.meta.env.VITE_GROUNDCOVER_API_KEY as string,
		dsn: `https://${import.meta.env.VITE_GROUNDCOVER_DSN}`,
		cluster: import.meta.env.VITE_GROUNDCOVER_CLUSTER as string,
		environment: import.meta.env.VITE_STAGE as string,
		appId: 'events-3',
		options: {
			// Dropped in the fetch/XHR patches, before an event is ever built.
			excludedUrls: [MEDIA_SEGMENT_URL],
			beforeSend: (event) => {
				if (event.type === 'exception') {
					return shouldSendException(event.attributes as ExceptionAttributes);
				}

				return true;
			}
		}
	});
}

type RumUserIdAttendee = {
	regUuid: string;
	adminUuid?: never;
};

type RumUserIdAdmin = {
	regUuid?: never;
	adminUuid: string;
};

type RumUserId = RumUserIdAdmin | RumUserIdAttendee;

// No PII
export const setRumUser = (user: RumUserId) => {
	if (GC_ENABLED) {
		if (user.adminUuid) {
			groundcover.identifyUser({ id: user.adminUuid, role: 'admin' });
		} else { 
			groundcover.identifyUser({ id: user.regUuid, role: 'attendee' });
		}
	}
};